import { RefObject, useEffect, useRef } from 'react';
import { getFocusableElements } from './use-focus-trap';

// How long to wait for the content of a panel that opened to render, which can
// depend on a request
const PANEL_RENDER_TIMEOUT = 5000;

interface UseFocusIntoPanelParams {
  /** The id of the open panel, or null when every panel is closed */
  activePanel: number | string | null;
  /** The element holding both the triggers and the panels */
  containerRef: RefObject<HTMLElement | null>;
  /**
   * Called when tabbing past the end of the panel moves focus to the next
   * trigger, to close the panel. Without it, the default tab order is kept.
   */
  onTabPastPanel?: () => void;
  /** The element holding the triggers, the panel is what follows it */
  triggersRef: RefObject<HTMLElement | null>;
}

// The focusable elements of the panel, which is what follows the triggers
const getPanelElements = (container: HTMLElement, triggers: HTMLElement) =>
  getFocusableElements(container).filter(
    element =>
      !triggers.contains(element) &&
      !!(
        triggers.compareDocumentPosition(element) &
        Node.DOCUMENT_POSITION_FOLLOWING
      ),
  );

/**
 * Moves focus to the first focusable element of a panel when it opens or is
 * swapped for another one, so keyboard users don't have to tab past the other
 * triggers to reach it. Only does so when focus was already in the container,
 * so panels opened without user interaction don't steal focus.
 */
const useFocusIntoPanel = ({
  activePanel,
  containerRef,
  onTabPastPanel,
  triggersRef,
}: UseFocusIntoPanelParams) => {
  const hasFocusWithinRef = useRef(false);
  const openTriggerRef = useRef<HTMLElement | null>(null);
  const previousPanelRef = useRef(activePanel);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const handleFocusIn = () => {
      hasFocusWithinRef.current = true;
    };

    // A focused element that gets removed (e.g. a next button whose panel is
    // swapped) has no next element, so focus only counts as gone when it moves
    // to an element outside the container
    const handleFocusOut = (event: FocusEvent) => {
      const nextFocusedElement = event.relatedTarget;

      if (
        nextFocusedElement instanceof Node &&
        !container.contains(nextFocusedElement)
      ) {
        hasFocusWithinRef.current = false;
      }
    };

    // Pressing outside, e.g. to close the panel, moves on as well, even when
    // nothing focusable is pressed and focus only falls back to the body
    const handleDocumentPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !container.contains(event.target)) {
        hasFocusWithinRef.current = false;
      }
    };

    // Safari doesn't focus buttons on click, so pressing one counts as well
    container.addEventListener('pointerdown', handleFocusIn);
    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);
    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => {
      container.removeEventListener('pointerdown', handleFocusIn);
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('pointerdown', handleDocumentPointerDown);
    };
  }, [containerRef]);

  useEffect(() => {
    const previousPanel = previousPanelRef.current;

    previousPanelRef.current = activePanel;

    if (activePanel === previousPanel) return;

    const container = containerRef.current;
    const triggers = triggersRef.current;

    if (!container || !triggers) return;

    if (activePanel === null) {
      const openTrigger = openTriggerRef.current;

      openTriggerRef.current = null;

      if (!openTrigger?.isConnected || !hasFocusWithinRef.current) return;

      const { activeElement } = document;
      const isFocusLost = !activeElement || activeElement === document.body;
      const isFocusInPanel =
        !isFocusLost &&
        container.contains(activeElement) &&
        !triggers.contains(activeElement);

      // Closing from inside the panel, e.g. with its close button, continues
      // from the trigger that opened it, as the panel's content is removed.
      // Focus already moved on, e.g. to the next trigger, is left alone
      if (isFocusInPanel || isFocusLost) {
        openTrigger.focus({ preventScroll: true });
      }

      return;
    }

    openTriggerRef.current = triggers.querySelector<HTMLElement>(
      '[aria-expanded="true"]',
    );

    if (!hasFocusWithinRef.current) return;

    // The element that opened the panel, usually its trigger
    const opener = document.activeElement;
    let focusedPanelElement: HTMLElement | null = null;

    const focusFirstPanelElement = () => {
      const { activeElement } = document;
      const isFocusOnOpener = !!opener && activeElement === opener;

      // Focus falls back to the body when the focused element is removed, e.g.
      // the content of the previous panel that was still animating out
      const isFocusLost =
        !activeElement ||
        activeElement === document.body ||
        (!!focusedPanelElement && !focusedPanelElement.isConnected);

      // The user moved on, so focus is left alone. Focus that left the
      // container counts too, as a panel unmounting later must not pull it back
      if (!hasFocusWithinRef.current || (!isFocusOnOpener && !isFocusLost)) {
        return;
      }

      const [firstPanelElement] = getPanelElements(container, triggers);

      if (!firstPanelElement) return;

      // The page scrolls the search bar into place on open, so focusing must
      // not scroll as well
      firstPanelElement.focus({ preventScroll: true });
      focusedPanelElement = firstPanelElement;
    };

    focusFirstPanelElement();

    // The panel content can render, or be revealed by a class change, a moment
    // after it opens, and the previous panel can still be animating out
    const observer = new MutationObserver(focusFirstPanelElement);
    const timeout = window.setTimeout(
      () => observer.disconnect(),
      PANEL_RENDER_TIMEOUT,
    );

    observer.observe(container, {
      attributeFilter: ['class', 'hidden', 'style'],
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [activePanel, containerRef, triggersRef]);

  // The panel comes after all the triggers in the DOM, so the tab order is
  // stitched back to trigger, panel, next trigger: from the open trigger into
  // the panel, past its end to the next trigger, before its start back to the
  // trigger that opened it
  useEffect(() => {
    const container = containerRef.current;
    const triggers = triggersRef.current;

    if (activePanel === null || !onTabPastPanel || !container || !triggers) {
      return;
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const panelElements = getPanelElements(container, triggers);
      const openTrigger = triggers.querySelector<HTMLElement>(
        '[aria-expanded="true"]',
      );

      if (!openTrigger || !panelElements.length) return;

      const { activeElement } = document;

      if (!event.shiftKey && activeElement === openTrigger) {
        event.preventDefault();
        panelElements[0]?.focus();

        return;
      }

      if (event.shiftKey && activeElement === panelElements[0]) {
        event.preventDefault();
        openTrigger.focus();

        return;
      }

      if (
        !event.shiftKey &&
        activeElement === panelElements[panelElements.length - 1]
      ) {
        const triggerElements = getFocusableElements(triggers);
        const nextTrigger =
          triggerElements[triggerElements.indexOf(openTrigger) + 1];

        if (!nextTrigger) return;

        event.preventDefault();
        nextTrigger.focus();
        onTabPastPanel();
      }
    };

    container.addEventListener('keydown', handleKeydown);

    return () => {
      container.removeEventListener('keydown', handleKeydown);
    };
  }, [activePanel, containerRef, onTabPastPanel, triggersRef]);
};

export default useFocusIntoPanel;
